'use client';

import React from 'react';
import ContentSection from '@/components/organisms/level3/ContentSection';
import styles from './CareerContent.module.scss';

interface CareerContentProps {
  description: string;
  jobDetails: {
    title: string;
    department: string;
    location: string;
    employmentType: string;
    experience: string;
    salary?: string;
  };
  responsibilities: string[];
  requirements: {
    required: string[];
    preferred: string[];
  };
  benefits: string[];
  workStyle: {
    title: string;
    description: string;
    features: string[];
  };
  applicationProcess: {
    steps: {
      step: number;
      title: string;
      description: string;
      duration?: string;
    }[];
    notes?: string[];
  };
  companyInfo?: {
    vision: string;
    mission: string;
    values: string[];
  };
}

export function CareerContent({
  description,
  jobDetails,
  responsibilities,
  requirements,
  benefits,
  workStyle,
  applicationProcess,
  companyInfo,
}: CareerContentProps) {
  return (
    <div className={styles.careerContent}>
      {/* Job Overview Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>職種概要</h2>
        <div className={styles.overview}>
          <p className={styles.description}>{description}</p>
          <div className={styles.jobDetailsGrid}>
            <div className={styles.jobDetail}>
              <span className={styles.label}>職種名</span>
              <span className={styles.value}>{jobDetails.title}</span>
            </div>
            <div className={styles.jobDetail}>
              <span className={styles.label}>部署</span>
              <span className={styles.value}>{jobDetails.department}</span>
            </div>
            <div className={styles.jobDetail}>
              <span className={styles.label}>勤務地</span>
              <span className={styles.value}>{jobDetails.location}</span>
            </div>
            <div className={styles.jobDetail}>
              <span className={styles.label}>雇用形態</span>
              <span className={styles.value}>{jobDetails.employmentType}</span>
            </div>
            <div className={styles.jobDetail}>
              <span className={styles.label}>経験年数</span>
              <span className={styles.value}>{jobDetails.experience}</span>
            </div>
            {jobDetails.salary && (
              <div className={styles.jobDetail}>
                <span className={styles.label}>給与</span>
                <span className={styles.value}>{jobDetails.salary}</span>
              </div>
            )}
          </div>
        </div>
      </ContentSection>

      {/* Responsibilities Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>主な業務内容</h2>
        <div className={styles.responsibilities}>
          <ul className={styles.responsibilityList}>
            {responsibilities.map((responsibility, index) => (
              <li key={index} className={styles.responsibilityItem}>
                <span className={styles.responsibilityIcon}>✓</span>
                <span className={styles.responsibilityText}>{responsibility}</span>
              </li>
            ))}
          </ul>
        </div>
      </ContentSection>

      {/* Requirements Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>応募要件</h2>
        <div className={styles.requirements}>
          <div className={styles.requirementCategory}>
            <h3 className={styles.requirementTitle}>必須要件</h3>
            <ul className={styles.requirementList}>
              {requirements.required.map((req, index) => (
                <li key={index} className={styles.requirementItem}>
                  <span className={styles.requirementIcon}>🔸</span>
                  <span className={styles.requirementText}>{req}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.requirementCategory}>
            <h3 className={styles.requirementTitle}>歓迎要件</h3>
            <ul className={styles.requirementList}>
              {requirements.preferred.map((req, index) => (
                <li key={index} className={styles.requirementItem}>
                  <span className={styles.requirementIcon}>🔹</span>
                  <span className={styles.requirementText}>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* Benefits Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>福利厚生・待遇</h2>
        <div className={styles.benefits}>
          <ul className={styles.benefitList}>
            {benefits.map((benefit, index) => (
              <li key={index} className={styles.benefitItem}>
                <span className={styles.benefitIcon}>🎯</span>
                <span className={styles.benefitText}>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </ContentSection>

      {/* Work Style Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>{workStyle.title}</h2>
        <div className={styles.workStyle}>
          <p className={styles.workStyleDescription}>{workStyle.description}</p>
          <div className={styles.workStyleFeatures}>
            {workStyle.features.map((feature, index) => (
              <div key={index} className={styles.workStyleFeature}>
                <span className={styles.featureIcon}>⚡</span>
                <span className={styles.featureText}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      {/* Application Process Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>選考プロセス</h2>
        <div className={styles.applicationProcess}>
          <div className={styles.processSteps}>
            {applicationProcess.steps.map((step, index) => (
              <div key={index} className={styles.processStep}>
                <div className={styles.stepNumber}>
                  <span>{step.step}</span>
                </div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                  {step.duration && (
                    <span className={styles.stepDuration}>所要時間: {step.duration}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {applicationProcess.notes && (
            <div className={styles.processNotes}>
              <h3 className={styles.notesTitle}>選考に関する注意事項</h3>
              <ul className={styles.notesList}>
                {applicationProcess.notes.map((note, index) => (
                  <li key={index} className={styles.noteItem}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ContentSection>

      {/* Company Information Section */}
      {companyInfo && (
        <ContentSection>
          <h2 className={styles.sectionTitle}>会社について</h2>
          <div className={styles.companyInfo}>
            <div className={styles.companyVision}>
              <h3 className={styles.companyInfoTitle}>ビジョン</h3>
              <p className={styles.companyInfoText}>{companyInfo.vision}</p>
            </div>
            <div className={styles.companyMission}>
              <h3 className={styles.companyInfoTitle}>ミッション</h3>
              <p className={styles.companyInfoText}>{companyInfo.mission}</p>
            </div>
            <div className={styles.companyValues}>
              <h3 className={styles.companyInfoTitle}>価値観</h3>
              <ul className={styles.valuesList}>
                {companyInfo.values.map((value, index) => (
                  <li key={index} className={styles.valueItem}>{value}</li>
                ))}
              </ul>
            </div>
          </div>
        </ContentSection>
      )}
    </div>
  );
}

export default CareerContent;